import React from 'react';
import PropTypes from 'prop-types';
import { useDesignSystem } from '../../context/DesignSystemContext';
import './taskConfirmation.scss';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { taskConfirmationI18n } from './i18n';

/**
 * TaskConfirmation
 *
 * Modal de confirmación para aprobar o rechazar una tarea.
 * Muestra icono, título, mensaje y botones de acción.
 *
 * Props:
 * - isOpen: boolean -> controla si el modal se muestra o no.
 * - mode: 'approve' | 'reject' -> define icono, color y textos por defecto.
 * - onConfirm: function -> callback al confirmar.
 * - onCancel: function -> callback al cancelar/cerrar.
 * - title: string (opcional) -> permite sobrescribir el título por defecto.
 * - description: string (opcional) -> permite sobrescribir el mensaje por defecto.
 */
const TaskConfirmation = ({
  isOpen,
  mode = 'approve',
  onConfirm,
  onCancel,
  title,
  description,
}) => {
  const { t } = useScopedTranslation(taskConfirmationI18n);
  const { Modal, Button, SvgIcon } = useDesignSystem?.() || {};

  if (!isOpen) return null;

  const isApprove = mode === 'approve';

  const iconName = isApprove ? 'circleCheck' : 'circleX';
  const iconBgClass = isApprove
    ? 'task-confirmation__icon-wrapper--success'
    : 'task-confirmation__icon-wrapper--danger';

  const resolvedTitle =
    title ||
    (isApprove ? t('tc_confirm_approve_title') : t('tc_confirm_reject_title'));

  const resolvedDescription =
    description ||
    (isApprove ? t('tc_confirm_approve_desc') : t('tc_confirm_reject_desc'));

  const confirmLabel = isApprove ? t('tc_approve') : t('tc_reject');

  const handleConfirm = () => {
    if (onConfirm) onConfirm();
  };

  const handleCancel = () => {
    if (onCancel) onCancel();
  };

  const footer = (
    <div className='d-flex gap-3'>
      <Button
        label={t('tc_confirm_cancel')}
        variant='secondary'
        onClick={handleCancel}
        size='sm'
      />
      <Button
        label={confirmLabel}
        size='sm'
        variant={isApprove ? 'primary' : 'danger'}
        onClick={handleConfirm}
      />
    </div>
  );

  if (Modal) {
    return (
      <Modal
        show={isOpen}
        onClose={handleCancel}
        subtitle={resolvedDescription}
        primaryBtnTxt={confirmLabel}
        primaryBtnAction={handleConfirm}
        secondaryBtnTxt={t('tc_confirm_cancel')}
        secondaryBtnAction={handleCancel}
        ctaAligment='right'
        width='480px'
        customFooter={footer}
      >
        <div className='task-confirmation__content d-flex align-items-start gap-3'>
          <div className={`task-confirmation__icon-wrapper ${iconBgClass}`}>
            <SvgIcon
              name={iconName}
              stroke={isApprove ? 'var(--success-500)' : 'var(--error-500)'}
            />
          </div>
          <div className='task-confirmation__body'>
            <h4 className='fw-semibold mb-1'>{resolvedTitle}</h4>
            <p className='font-light caption mb-0'>{resolvedDescription}</p>
          </div>
        </div>
      </Modal>
    );
  }
};

TaskConfirmation.propTypes = {
  isOpen: PropTypes.bool,
  mode: PropTypes.oneOf(['approve', 'reject']),
  onConfirm: PropTypes.func,
  onCancel: PropTypes.func,
  title: PropTypes.string,
  description: PropTypes.string,
};

export default TaskConfirmation;
